// ─── 🎾 Il promoter, nella scheda del socio (16/10/2026) ─────────────────────
//
// Mockup «Promoter»: «🎾 Promoter dal 15 settembre», la settimana, il risparmio
// del mese e in tutto, le ultime settimane, e «⏸️ Metti in pausa» (vacanza,
// infortunio: ferma il conteggio e gli avvisi, la gratuità resta). Per lo staff
// (ST) niente minimo né pausa. Si aggiunge e si toglie da Regole per i soci ›
// Promoter. Il riquadro c'è solo per chi è nella lista.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatCents, type PromoterDelSocio as Promoter } from '@pmo/shared';
import { chiama } from '../../lib/api.js';
import { Guasto } from '../../components/pezzi.js';

const giorno = (d: string) => d.split('-').reverse().join('/');

export function PromoterDelSocio({
  id,
  modificabile,
}: {
  id: string;
  modificabile: boolean;
}): React.JSX.Element | null {
  const qc = useQueryClient();
  const [apre, setApre] = useState(false);
  const [dal, setDal] = useState('');
  const [al, setAl] = useState('');
  const p = useQuery({
    queryKey: ['promoter-socio', id],
    queryFn: () => chiama<Promoter>('GET', `/members/${id}/promoter`),
  });
  const pausa = useMutation({
    meta: { fatto: (_d: unknown, v: unknown) => (v === null ? 'pausa tolta' : 'pausa segnata') },
    mutationFn: (v: { dal: string; al: string } | null) =>
      chiama<Promoter>('PUT', `/members/${id}/promoter/pausa`, { pausa: v }),
    onSuccess: (dopo) => {
      qc.setQueryData(['promoter-socio', id], dopo);
      void qc.invalidateQueries({ queryKey: ['promoter'] });
      setApre(false);
    },
  });
  if (p.error) return <Guasto errore={p.error} />;
  if (!p.data || !p.data.promoter) return null;
  const x = p.data;

  return (
    <section className="riquadro">
      <h3>
        🎾 {x.staff ? 'Staff' : 'Promoter'} dal {x.dal ? giorno(x.dal) : ''}
        {x.al ? ` al ${giorno(x.al)}` : ''} <span className="etichetta-pr">{x.staff ? 'ST' : 'PR'}</span>
      </h3>
      {x.stato === 'da_iniziare' && (
        <p className="piccolo">
          📅 Il periodo comincia il {x.dal ? giorno(x.dal) : ''}: fino ad allora paga come tutti.
        </p>
      )}
      {x.stato === 'finito' && <p className="piccolo">📅 Il periodo è finito: adesso paga come tutti.</p>}
      {x.staff ? (
        <p className="piccolo">Non paga mai la quota campo, e non rientra nel minimo di partite.</p>
      ) : (
        <p className="piccolo">
          Questa settimana:{' '}
          <strong>{x.stato === 'pausa' ? 'in pausa' : `${x.giocate} partite su ${x.minimo}`}</strong>
        </p>
      )}
      <p className="piccolo">
        Risparmio del mese <strong>{formatCents(x.risparmioMeseCents)}</strong>, in tutto{' '}
        <strong>{formatCents(x.risparmioTotaleCents)}</strong>
      </p>
      {!x.staff && x.ultime.length > 0 && (
        <p className="tenue piccolo">
          Ultime settimane: {x.ultime.map((n) => (n === null ? '⏸️' : String(n))).join(' · ')}
        </p>
      )}
      {!x.staff && x.pausa && (
        <p className="piccolo">
          ⏸️ In pausa dal {giorno(x.pausa.dal)} al {giorno(x.pausa.al)}: niente conteggio e niente avvisi, la
          gratuità resta.
        </p>
      )}
      {modificabile && !x.staff && !apre && (
        <p>
          {x.pausa ? (
            <button
              type="button"
              className="bordato piccolo"
              disabled={pausa.isPending}
              onClick={() => pausa.mutate(null)}
            >
              Togli la pausa
            </button>
          ) : (
            <button
              type="button"
              className="bordato piccolo"
              onClick={() => {
                setDal('');
                setAl('');
                setApre(true);
              }}
            >
              ⏸️ Metti in pausa
            </button>
          )}
        </p>
      )}
      {apre && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (dal && al && dal <= al) pausa.mutate({ dal, al });
          }}
        >
          <label className="campo">
            <span>Dal</span>
            <input type="date" value={dal} onChange={(e) => setDal(e.target.value)} />
          </label>
          <label className="campo">
            <span>Al</span>
            <input type="date" value={al} min={dal} onChange={(e) => setAl(e.target.value)} />
          </label>
          <button type="submit" disabled={!dal || !al || dal > al || pausa.isPending}>
            Salva
          </button>{' '}
          <button type="button" className="bordato" onClick={() => setApre(false)}>
            Lascia stare
          </button>
        </form>
      )}
      <p className="tenue piccolo">
        Si aggiunge e si toglie in{' '}
        <Link to="/impostazioni/borsellino/promoter">Regole per i soci › Promoter</Link>.
      </p>
      <Guasto errore={pausa.error} />
    </section>
  );
}
